import ClipboardJS from 'clipboard';
import { Controller } from 'stimulus';
import { announce } from '../javascript/lib/a11y/announcement';
import { t } from '../javascript/lib/i18n';
import Tooltip from 'bootstrap/js/src/tooltip';

/**
 * Generic setup for ClipboardJS that'll recognise
 * any button with a `data-clipboard-target` or `data-clipboard-text`
 * attribute and dispatch an event for hooking Stimulus actions
 * https://clipboardjs.com/
 *
 */
export default class extends Controller {
  static values = { copiedLabel: { type: String, default: '' } };

  connect() {
    this.clipboard = new ClipboardJS(this.element, {
      text(trigger) {
        // Gather content to copy ourselves, as Clipboard.js way
        // sets plenty of whitespace at the start of the strings
        if (trigger.hasAttribute('data-clipboard-target')) {
          return [
            ...document.querySelectorAll(
              trigger.getAttribute('data-clipboard-target')
            ),
          ]
            .map(contentToCopy)
            .map(normalizeWhitespace)
            .join('\n\n');
        } else if (trigger.hasAttribute('data-clipboard-text')) {
          return trigger.getAttribute('data-clipboard-text');
        }
      },
      container: this.container,
    });

    this.clipboard.on('success', (copy) => {
      copy.clearSelection();
      copy.trigger.dispatchEvent(
        new CustomEvent('clipboard-success', {
          bubbles: true,
        })
      );

      announce(t('js.common.copied'));
      this.flashTooltip();
    });

    this.clipboard.on('error', function (copy) {
      copy.trigger.dispatchEvent(
        new CustomEvent('clipboard-error', {
          bubbles: true,
        })
      );
    });
  }

  disconnect() {
    if (this.clipboard) {
      this.clipboard.destroy();
    }
    clearTimeout(this._tooltipTimer);
  }

  // When the element has a Bootstrap tooltip, briefly swap it to "Copied!".
  flashTooltip() {
    const tooltip = Tooltip.getInstance(this.element);
    if (!tooltip) return;

    const original =
      this.element.getAttribute('title') ||
      this.element.getAttribute('data-bs-original-title');

    this.element.setAttribute(
      'data-bs-original-title',
      this.copiedLabelValue || t('js.common.copied')
    );
    tooltip.show();

    clearTimeout(this._tooltipTimer);
    this._tooltipTimer = setTimeout(() => {
      tooltip.hide();
      this.element.setAttribute('data-bs-original-title', original);
      this.element.setAttribute('title', original);
    }, 1500);
  }

  get container() {
    if (this.element.closest('#turbo-offcanvas')) {
      return this.element.closest('#turbo-offcanvas');
    } else if (this.element.closest('#turbo-modal')) {
      return this.element.closest('#turbo-modal');
    } else if (this.element.closest('.modal')) {
      return this.element.closest('.modal');
    } else {
      return document.documentElement;
    }
  }
}

function normalizeWhitespace(text) {
  return text
    .replace(/[\s\n]+/g, ' ')
    .replace(/^\s/, '')
    .replace(/\s$/, '');
}

// Variation on https://github.com/github/clipboard-copy-element/blob/1bc0008929be00c219a208f63a7ee991b4c2b2f6/src/clipboard-copy-element.ts#L25
function contentToCopy(element) {
  if ('value' in element) {
    return element.value;
  } else if (
    element instanceof HTMLAnchorElement &&
    element.hasAttribute('href')
  ) {
    return element.href;
  } else {
    return element.textContent;
  }
}
